import React, { useEffect, useState, useMemo, useRef } from 'react';
import axios from 'axios';
import { Sparkles, Upload, Loader2, Map, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/floorplan`;

/**
 * FloorplanStoryteller — public read of the AI walkthrough on /property/:id,
 * with an owner-only upload/regenerate widget. Country-aware (the backend
 * reads property.location.country and routes to the matching prompt).
 */
export default function FloorplanStoryteller({ property }) {
  const propertyId = property?._id || property?.id;
  const { user, isAuthenticated } = useAuth();
  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);  // collapse long stories below the fold
  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState(null);
  const fileRef = useRef(null);

  const isOwner = useMemo(() => {
    if (!isAuthenticated || !user || !property) return false;
    const uid = user._id || user.id;
    return uid && (uid === property.userId || uid === property.user_id);
  }, [user, isAuthenticated, property]);

  useEffect(() => {
    if (!propertyId) return;
    setLoading(true);
    axios.get(`${API}/${propertyId}`)
      .then((r) => setStory(r.data))
      .catch(() => setStory(null))
      .finally(() => setLoading(false));
  }, [propertyId]);

  const submit = async () => {
    if (!file) { toast.error('Pick a floor-plan image first.'); return; }
    if (file.size > 8 * 1024 * 1024) { toast.error('Floor plan must be under 8MB.'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('property_id', propertyId);
      fd.append('floor_plan', file);
      const { data } = await axios.post(`${API}/generate`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60_000,
      });
      setStory({
        ...data,
        image_url: `/api/floorplan/image/${data.image_hash}`,
      });
      toast.success(data.cached ? 'Pulled from cache — no LLM credit used.'
                                  : 'Walkthrough generated.');
      setFile(null);
      if (fileRef.current) fileRef.current.value = '';
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 401 || status === 403) toast.error('Sign in as the listing owner to generate.');
      else if (status === 429) toast.error(typeof detail === 'string' ? detail : 'Daily limit reached.');
      else if (status === 413) toast.error('Floor plan exceeds 8MB.');
      else toast.error(typeof detail === 'string' ? detail : 'Generation failed. Try a clearer image.');
    } finally {
      setUploading(false);
    }
  };

  // Nothing to show? Hide entirely unless the viewer is the owner.
  if (!story && !isOwner && !loading) return null;

  const paragraphs = story?.story ? story.story.split(/\n\n+/) : [];
  const preview = paragraphs.slice(0, 2).join('\n\n');
  const rest = paragraphs.slice(2).join('\n\n');

  return (
    <Card className="p-5 md:p-6 rounded-2xl border-stone-200/80" data-testid="floorplan-storyteller">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-[#F5EFFF]">
            <Map className="h-4 w-4 text-[#7042cc]" />
          </span>
          <div>
            <div className="flex items-center gap-1.5">
              <h3 className="text-lg font-semibold text-stone-900">AI Walkthrough</h3>
              <span className="px-1.5 py-0.5 rounded bg-[#7042cc] text-white text-[9px] font-bold tracking-wider">NEW</span>
            </div>
            {story?.market_label && (
              <p className="text-xs text-stone-500">{story.market_label} narrative · {story.word_count} words</p>
            )}
          </div>
        </div>
        {isOwner && story && (
          <button
            onClick={() => fileRef.current?.click()}
            className="text-xs font-semibold text-[#7042cc] hover:underline inline-flex items-center gap-1"
            data-testid="floorplan-regenerate-btn"
          >
            <RefreshCw className="h-3 w-3" /> Regenerate
          </button>
        )}
      </div>

      {loading && (
        <div className="py-8 text-center text-stone-400 text-sm">Loading walkthrough…</div>
      )}

      {!loading && story && (
        <div className="space-y-3">
          {story.image_url && (
            <img
              src={`${BACKEND_URL}${story.image_url}`}
              alt="Floor plan"
              className="rounded-xl border border-stone-200 max-h-72 w-full object-contain bg-stone-50"
              data-testid="floorplan-image"
            />
          )}
          <div className="text-stone-800 leading-relaxed whitespace-pre-wrap" data-testid="floorplan-story-text">
            {preview}
            {open && rest && <>{'\n\n'}{rest}</>}
          </div>
          {rest && (
            <button
              onClick={() => setOpen(!open)}
              className="text-sm font-semibold text-[#7042cc] hover:underline inline-flex items-center gap-1"
              data-testid="floorplan-toggle"
            >
              {open ? (<><ChevronUp className="h-3.5 w-3.5" /> Read less</>) :
                       (<><ChevronDown className="h-3.5 w-3.5" /> Read full walkthrough</>)}
            </button>
          )}
        </div>
      )}

      {!loading && !story && isOwner && (
        <div className="py-3 text-sm text-stone-600">
          <p className="mb-3">
            Upload this listing's floor plan and our AI writes a warm, sensory walkthrough
            buyers love — in under a minute. Saves you 30+ minutes per listing.
          </p>
          <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="text-sm text-stone-700 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-[#F5EFFF] file:text-[#7042cc]"
              data-testid="floorplan-file-input"
            />
            <Button
              onClick={submit}
              disabled={!file || uploading}
              className="bg-[#7042cc] hover:bg-[#5b35a8] text-white"
              data-testid="floorplan-generate-btn"
            >
              {uploading ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" />Generating…</>) :
                            (<><Sparkles className="h-4 w-4 mr-1.5" />Generate walkthrough</>)}
            </Button>
          </div>
          <p className="mt-2 text-xs text-stone-400">JPG / PNG / WEBP · up to 8MB · AI vision</p>
        </div>
      )}

      {/* Hidden input for the Regenerate flow when story already exists */}
      {isOwner && story && (
        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          onChange={(e) => { const f = e.target.files?.[0]; if (f) { setFile(f); setTimeout(submit, 0); } }}
          className="hidden"
          data-testid="floorplan-regenerate-file-input"
        />
      )}
    </Card>
  );
}
